import React, { useContext, useEffect, useRef, useState } from "react";

import {
  Button,
  Grid,
  LinearProgress,
  Paper,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Clipboard } from "react-feather";
import mainStyles from "styles";

import { ResponseSet } from "@safetyculture/s12-apis-connect-web/s12/responseset/v1/responseset_pb";

import { DocumentSearch } from "./constants";
import { listOrgGrs } from "api/endpoints/grs";
import { getOrganizationIdentity } from "api/endpoints/organisation";
import { callOptionsHeaders } from "api/helpers";
import DisplayError from "components/shared/display-error";
import { DEFAULT_REGION_CODE } from "constants/index";
import { GrsSearchQueryContext } from "context/grs-search-context";
import { SnackbarContext } from "context/snackbar-context";
import { mapRegionStringToRegionCode } from "helpers/env-values-helper";
import {
  convertUUIDtoS12Id,
  validateS12ID,
  validateUUID,
} from "helpers/s12-helpers";

const TOTAL_ITEMS_UNINITIALISED = -1;

const GrsSearch = () => {
  const theme = useTheme();
  const classes = mainStyles();

  const { grsSearchQueryState, setGrsSearchQueryState } = useContext(
    GrsSearchQueryContext,
  );
  const { copyToClipboard, showError } = useContext(SnackbarContext);

  const [errorValidationMessage, setErrorValidationMessage] = useState("");
  const [grsSearchFormState, setGrsSearchFormState] =
    useState(grsSearchQueryState);

  const [grsList, setGrsList] = useState<ResponseSet[]>([]);
  const [totalItems, setTotalItems] = useState(TOTAL_ITEMS_UNINITIALISED);
  const [isLoading, setIsLoading] = useState(false);
  const isFirstRender = useRef(true);

  const handleChange = (
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent,
    key: string,
  ) => {
    let value;
    switch (key) {
      case "orgID":
        value = event.target.value.trim();
        break;
      default:
        value = event.target.value;
    }
    setGrsSearchFormState({
      ...grsSearchFormState,
      [key]: value,
    });
  };

  const isValidInput = () => {
    if (!grsSearchFormState.orgID) {
      setErrorValidationMessage("Org ID cannot be empty");
      return false;
    }
    if (
      !validateUUID(grsSearchFormState.orgID) &&
      !validateS12ID(grsSearchFormState.orgID)
    ) {
      setErrorValidationMessage("Org ID is not a valid UUID or S12 ID");
      return false;
    }

    return true;
  };

  const handleListOrgGrs = async () => {
    isFirstRender.current = false;

    if (!isValidInput()) {
      return;
    }

    setErrorValidationMessage("");
    setIsLoading(true);

    const orgS12ID = convertUUIDtoS12Id(
      grsSearchFormState.orgID,
      DocumentSearch.Role,
    );
    const orgResp = await getOrganizationIdentity(
      { orgId: orgS12ID },
      callOptionsHeaders(DEFAULT_REGION_CODE, orgS12ID),
    ).catch(showError);
    if (!orgResp?.region) {
      setIsLoading(false);
      return;
    }
    const region = mapRegionStringToRegionCode(orgResp.region);

    listOrgGrs({ orgId: orgS12ID }, callOptionsHeaders(region, orgS12ID))
      .then((resp) => {
        setGrsList(resp.grs);
        setTotalItems(resp.grs.length);

        window.history.replaceState(
          null,
          "",
          `${location.pathname}?roleId=${grsSearchFormState.orgID}`,
        );
      })
      .catch(showError)
      .finally(() => setIsLoading(false));
  };

  const clearForm = () => {
    setErrorValidationMessage("");
    setGrsSearchQueryState({
      ...grsSearchQueryState,
    });
    setGrsSearchFormState({
      ...grsSearchFormState,
    });

    isFirstRender.current = true;
    setGrsList([]);
    setTotalItems(TOTAL_ITEMS_UNINITIALISED);
  };

  useEffect(() => {
    if (!grsSearchQueryState.orgID && isFirstRender.current) {
      return;
    }

    setGrsSearchFormState(grsSearchQueryState);
    handleListOrgGrs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grsSearchQueryState]);

  const displayGrsRow = (grs: ResponseSet) => {
    return (
      <TableRow>
        <TableCell>
          <a
            href={`/search/doc/${grs.id ?? ""}`}
            className="remove-link-style"
            data-testid={`grs-link-${grs.id}`}
          >
            <Typography color="primary" fontWeight="bold" variant="subtitle1">
              {grs.name}
            </Typography>
          </a>
          <Typography
            variant="body2"
            align="left"
            sx={classes.clickable}
            onClick={() => copyToClipboard(grs.id ?? "")}
            noWrap={true}
          >
            {grs.id}
            <Clipboard
              color={theme.palette.text.primary}
              size={"1rem"}
              className="position-icon"
            />
          </Typography>
        </TableCell>
        <TableCell>
          {grs.responses.map((response, index) => (
            <div key={index}>{response.label}</div>
          ))}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Stack spacing={2} my={2}>
          <Stack direction="row" spacing={2} justifyContent="center">
            <TextField
              id="roleId-input"
              label="Org ID"
              value={grsSearchFormState.orgID}
              onChange={(event) => handleChange(event, "orgID")}
              variant="outlined"
              size="small"
              sx={{ width: 500, fullWidth: "100%" }}
              inputProps={{ "data-anchor": "org-id-input" }}
              onKeyUp={(event) => {
                if (event.key === "Enter") {
                  handleListOrgGrs();
                }
              }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleListOrgGrs}
              data-anchor="grs-search-btn"
              data-testid="grs-search-btn"
              disabled={!grsSearchFormState.orgID}
            >
              Search
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={clearForm}
              data-anchor="grs-search-clear-btn"
              data-testid="grs-search-clear-btn"
            >
              Clear
            </Button>
          </Stack>
        </Stack>

        <DisplayError errorMessage={errorValidationMessage} />

        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell style={{ width: "30%" }}>
                  <b>Name</b>
                </TableCell>
                <TableCell style={{ width: "70%" }}>
                  <b>Responses</b>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              ) : (
                grsList.map((grs) => displayGrsRow(grs))
              )}
            </TableBody>
          </Table>
          {!isLoading && totalItems === 0 && !isFirstRender.current && (
            <Typography py={3}>
              <b>No GRS found</b>
            </Typography>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default GrsSearch;
